// eslint-disable-next-line import/no-extraneous-dependencies
import barba from '@barba/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import initForm from './form';
import toggleNav from '../components/c-toggle-nav';
import spotify from '../components/c-spotify';
import loadMore from '../components/c-load-more';
import { pinTab, initServicesScroll } from '../../../blocks/services/scripts/services';
import { initProjectScroll } from '../../../blocks/project-scroll/scripts/project-scroll';
import headerVerticalScroll from '../components/c-header-vertical-scroll';
import setVideoCurrentTime from '../utils/setVideoCurrentTime';
import homeScripts from '../pages/home';
import projectScripts from '../pages/project';
import aboutScripts from '../pages/about';
import careersScripts from '../pages/careers';
import contactsScripts from '../pages/contacts';
import solutionsScripts from '../pages/solutions';
import defaultScripts from '../pages/default';
import { infoService, stickyMenuBgColorChangeByScroll } from '../../../blocks/intro-service/scripts/intro-service';
import cAppeared from '../components/c-appeared';
import blogScript from '../pages/blog';

const scrollToRightSide = (block) => {
  const screenWidth = document.documentElement.clientWidth;
  const blockRightOffset = block.getBoundingClientRect().right;
  const scrollOffset = screenWidth - blockRightOffset;
  window.scrollTo(0, window.scrollY - scrollOffset);
};

const initServicesBlock = () => {
  setTimeout(() => {
    const header = document?.querySelector('.header');
    const introService = document?.querySelector('.intro-service__anchor');

    if (!header || !introService) return;

    stickyMenuBgColorChangeByScroll(header, introService);
    initServicesScroll();
  }, 0);
};

const initProjectScrollBlock = () => {
  setTimeout(() => {
    const projectScroll = document?.querySelector('.project-scroll');

    if (!projectScroll) return;

    initProjectScroll();
  }, 0);
};

export default function () {
  barba.hooks.afterEnter(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });

    cAppeared();
    initServicesBlock();
    initProjectScrollBlock();
  });

  barba.hooks.beforeEnter(({ next }) => {
    const matches = next.html.match(/<body.+?class="([^""]*)"/i);
    document.body.setAttribute('class', (matches && matches.at(1)) ?? '');
    toggleNav();
  });

  barba.hooks.after((data) => {
    headerVerticalScroll();
    pinTab();

    if (typeof wpcf7 !== 'undefined') {
      initForm();
    }

    if (data.next.namespace === 'careers') {
      ScrollTrigger.getAll().forEach((trigger) => {
        trigger.refresh();
      });
    }
  });

  barba.init({
    prevent: ({ el }) => (el.classList && el.classList.contains('ab-item')) || (el.href && el.href.toLowerCase().includes('.html')),
    requestError: (trigger, action, url, response) => {
      if (action === 'click' && response.status && response.status === 404) {
        barba.go(window.location.href);
      }
    },
    views: [
      {
        namespace: 'home',
        afterEnter() {
          homeScripts();
        },
      },
      {
        namespace: 'project',
        afterEnter() {
          projectScripts();
        },
      },
      {
        namespace: 'about',
        afterEnter() {
          aboutScripts();
        },
      },
      {
        namespace: 'contacts',
        afterEnter() {
          document.querySelectorAll('link[dynamic-href]').forEach((item) => {
            if (!item.getAttribute('href')) {
              item.setAttribute('href', item.getAttribute('dynamic-href'));
              item.removeAttribute('dynamic-href');
            }
          });

          document.querySelectorAll('script[dynamic-src]').forEach((item) => {
            if (!item.getAttribute('src')) {
              item.setAttribute('src', item.getAttribute('dynamic-src'));
              item.removeAttribute('dynamic-src');
              item.removeAttribute('defer');
            }
          });
          contactsScripts();
        },
      },
      {
        namespace: 'careers',
        afterEnter() {
          careersScripts();
        },
      },
      {
        namespace: 'solutions',
        afterEnter() {
          solutionsScripts();
          infoService();
        },
      },
      {
        namespace: 'blog',
        afterEnter() {
          blogScript();
          loadMore();
        },
      },
      {
        namespace: 'podcast',
        afterEnter() {
          spotify();
        },
      },
      {
        namespace: 'default',
        afterEnter() {
          defaultScripts();
        },
      },
    ],
    transitions: [
      {
        name: 'home',
        to: {
          namespace: ['project'],
        },
        sync: true,
        leave({ trigger, current, next }) {
          if (typeof trigger === 'object') {
            if (trigger.hasAttribute('data-project')) {
              scrollToRightSide(trigger);

              // eslint-disable-next-line
              current.container.style.position = 'relative';
              // eslint-disable-next-line
              current.container.style.zIndex = '1';

              const descriptionElement = trigger.querySelector('[data-project-description]');
              // eslint-disable-next-line max-len
              const descriptionElementWidth = parseInt(getComputedStyle(descriptionElement).width, 10);

              const triggerVideoSelector = trigger.querySelector('[data-project-media]');
              const nextVideoSelector = next.container.querySelector('[data-cards-media]');
              setVideoCurrentTime(trigger, triggerVideoSelector, nextVideoSelector);

              next.container.querySelector('.project-hero__media').classList.add('project-hero__media--overlay');

              return gsap.timeline()
                .to(trigger.querySelector('[data-project-info]'), {
                  autoAlpha: 0,
                  duration: 0.4,
                  delay: 1,
                })
                .to(trigger.querySelector('[data-project-description]'), {
                  x: 0,
                  duration: 0.5,
                  delay: 1,
                }, 0)
                .to(trigger.querySelector('[data-project-media]'), {
                  width: `calc(100% - ${descriptionElementWidth}px)`,
                  duration: 0.5,
                  delay: 1,
                }, 0);
            }

            if (trigger.hasAttribute('data-project-card')) {
              scrollToRightSide(trigger);

              // eslint-disable-next-line
              current.container.style.position = 'relative';
              // eslint-disable-next-line
              current.container.style.zIndex = '1';

              const descriptionElement = trigger.querySelector('[data-project-card-description]');
              // eslint-disable-next-line max-len
              const descriptionElementWidth = parseInt(getComputedStyle(descriptionElement).width, 10);

              const triggerVideoSelector = trigger.querySelector('[data-cards-media]');
              const nextVideoSelector = next.container.querySelector('[data-cards-media]');
              setVideoCurrentTime(trigger, triggerVideoSelector, nextVideoSelector);

              return gsap.timeline()
                .set(trigger.querySelector('[data-cards-media-animation]'), {
                  autoAlpha: 1,
                })
                .set(trigger.querySelector('[data-cards-media]'), {
                  autoAlpha: 0,
                })
                .to(trigger.querySelector('[data-cards-media-animation]'), {
                  width: `calc(100vw - ${descriptionElementWidth - 30}px)`,
                  height: '100vh',
                  duration: 0.5,
                  delay: 1,
                })
                .to(descriptionElement, {
                  transform: `translateX(calc(-100vw + ${descriptionElementWidth}px)`,
                  autoAlpha: 1,
                  duration: 0.5,
                  delay: 1,

                }, 0);
            }
          }

          return gsap.to('[data-page-transitions]', {
            x: '0',
            duration: 1,
            ease: 'power2.inOut',
          });
        },
        after() {
          return gsap.to('[data-page-transitions]', {
            autoAlpha: '0',
            duration: 0.5,
            delay: 1,
            clearProps: 'all',
          });
        },
      },
      {
        sync: true,
        leave() {
          return gsap.to('[data-page-transitions]', {
            x: '0',
            duration: 0.8,
            ease: 'easeInOut',
          });
        },
        after() {
          const mm = gsap.matchMedia();
          const toggleViewBreakPoint = 576;

          return mm.add({
            isMobile: `(max-width: ${toggleViewBreakPoint - 1}px)`,
            isDesktop: `(min-width: ${toggleViewBreakPoint}px)`,
          }, (context) => {
            const { isMobile } = context.conditions;

            gsap.timeline()
              .to('[data-page-transitions]', {
                autoAlpha: '0',
                duration: 0.4,
                clearProps: 'all',
              })
              .fromTo('[data-header]', {
                x: isMobile ? 0 : -100,
                y: isMobile ? -100 : 0,
              }, {
                x: 0,
                y: 0,
                duration: 1.2,
                clearProps: 'all',
              }, -1);
          });
        },
      },
    ],
  });
}
